import { z } from 'zod';
/**
 * Contratos da camada de tempo real: os avisos do banco.
 *
 * Duas regras atravessam o arquivo.
 *
 * A primeira: **o aviso nao e um lancamento**. Nada aqui cria, edita ou apaga
 * transacao. A entrada e so o texto que o banco mandou, mais o suficiente
 * para saber que aviso e aquele. A leitura, a conversao em provisorio e a
 * conciliacao vem depois, em codigo que pode ser corrigido sem que o material
 * capturado se perca.
 *
 * A segunda: **a origem diz quem o aviso e**. A deduplicacao nao olha o
 * conteudo. O servico de notificacao do Android reentrega o mesmo aviso
 * quando ele e atualizado, e dois Pix de R$ 50,00 no mesmo minuto sao dois
 * Pix — deduplicar por texto erraria nas duas pontas. Por isso o aparelho
 * manda a identidade que ele mesmo da ao aviso.
 */
export const origemDoAvisoSchema = z.enum(['ANDROID', 'EMAIL', 'MANUAL']);
export const estadoDoAvisoSchema = z.enum([
    'RECEBIDO',
    'PROVISORIO',
    'ILEGIVEL',
    'IRRELEVANTE',
    'CONCILIADO',
    'DESCARTADO',
]);
/**
 * Quantos avisos cabem num envio.
 *
 * O aparelho acumula quando fica sem rede, entao o envio tem de ser em lote.
 * O teto existe para o corpo da requisicao ter tamanho previsivel: o que nao
 * couber fica na fila do aparelho e vai no proximo envio, que e exatamente o
 * comportamento que ele ja precisa ter para sobreviver a uma falha de rede.
 */
export const LOTE_MAXIMO_DE_AVISOS = 50;
/** Limite do texto cru. Notificacao de banco nao chega perto disso. */
const TEXTO_MAXIMO = 2000;
export const avisoCapturadoSchema = z.object({
    origem: origemDoAvisoSchema.default('ANDROID'),
    /** Qual aparelho mandou. Permite desligar uma fonte sem desligar as outras. */
    dispositivo: z.string().trim().min(1).max(80),
    /**
     * A identidade do aviso na origem.
     *
     * No Android e a chave da notificacao somada ao instante em que ela foi
     * postada — as duas coisas que o proprio sistema operacional fornece e que
     * nao mudam quando a notificacao e reentregue.
     */
    chaveNaOrigem: z.string().trim().min(1).max(200),
    /** Quem emitiu: o pacote do aplicativo, ou o remetente do e-mail. */
    emissor: z.string().trim().min(1).max(120),
    titulo: z.string().trim().max(300).default(''),
    texto: z.string().trim().min(1).max(TEXTO_MAXIMO),
    /** Quando o aviso apareceu no aparelho, em ISO 8601 com fuso. */
    ocorridoEm: z.string().datetime({ offset: true }),
});
export const receberAvisosSchema = z.object({
    avisos: z.array(avisoCapturadoSchema).min(1).max(LOTE_MAXIMO_DE_AVISOS),
});
/**
 * A resposta separa o que entrou do que ja existia.
 *
 * O aparelho precisa da diferenca: "repetido" e sucesso, e insistir no reenvio
 * de algo que o servidor ja tem so gasta bateria. Sem o numero, a unica saida
 * do aplicativo seria reenviar ate alguem reclamar.
 */
export const recepcaoDeAvisosSchema = z.object({
    recebidos: z.number().int(),
    repetidos: z.number().int(),
});
export const avisoSchema = z.object({
    id: z.string(),
    origem: origemDoAvisoSchema,
    dispositivo: z.string(),
    emissor: z.string(),
    titulo: z.string(),
    texto: z.string(),
    ocorridoEm: z.string(),
    recebidoEm: z.string(),
    estado: estadoDoAvisoSchema,
    leitor: z.string().nullable(),
    /** Centavos como texto, pela mesma razao do resto da API: bigint nao e JSON. */
    valorCents: z.string().nullable(),
    direcao: z.enum(['INCOME', 'EXPENSE', 'TRANSFER']).nullable(),
    contraparte: z.string().nullable(),
    metodo: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    transactionId: z.string().nullable(),
    conciliadoEm: z.string().nullable(),
    observacao: z.string().nullable(),
});
export const listarAvisosSchema = z.object({
    estado: estadoDoAvisoSchema.optional(),
    dispositivo: z.string().trim().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const avisoListSchema = z.object({
    avisos: z.array(avisoSchema),
    /**
     * Quantos avisos nenhum leitor conseguiu ler.
     *
     * E o numero que diz se vale escrever leitor novo, e qual. Enquanto nao
     * existir leitor nenhum, ele e igual ao total — e e assim que tem de ser.
     */
    ilegiveis: z.number().int(),
});
export const descartarAvisoSchema = z.object({
    motivo: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=avisos.js.map